import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  LineElement, PointElement, Title, Tooltip, Legend, Filler,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import axiosInstance from "../../common/axiosInstance";
import { usePremisesStore } from "../../store/store";
import { DotLoader } from "../../components/loader/Loader";
import {
  MONTH_NAMES, getPremiseName, getReportTypeConfig,
  safeFetchAll, getMonthFromDate,
} from "./constants";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler);

// ─── Types ────────────────────────────────────────────────────────────────────
type TimeRange = "1M" | "3M" | "6M" | "1Y" | "ALL";

interface MonthDataPoint {
  month: number; year: number; label: string; count: number;
  avgPct?: number; avgScore?: number;
  avgCompliedPct?: number; avgNotCompliedPct?: number; avgPartialPct?: number;
  avgComplied?: number; avgNotComplied?: number;
  // Equipment inspections
  passCount?: number; failCount?: number; attentionCount?: number;
  passPct?: number;
  // Fire safety scorecard — audit scores per category
  extA?: number | null; extB?: number | null; extC?: number | null;
  intA?: number | null; intB?: number | null; intC?: number | null;
  extAvgPct?: number | null; intAvgPct?: number | null;
  // Cat-A NC completion % (for the 3-bar chart)
  extACatNcPct?: number | null; intACatNcPct?: number | null;
  incidentPct?: number | null;
  // MIS — separate near miss and major incident tracking
  nearMissPct?: number | null; nearMissActual?: number | null;
  majorPct?: number | null; majorActual?: number | null;
}

interface EquipTypeTrend {
  id: string; name: string;
  data: { month: number; year: number; label: string; count: number; passPct: number | null }[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getMonthYear = (s: string): { month: number; year: number } | null => {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return { month: d.getMonth() + 1, year: d.getFullYear() };
};

const getMonthsForRange = (range: TimeRange, selectedYear: number | null): { month: number; year: number }[] => {
  if (selectedYear) {
    // Show all 12 months of the selected year
    return Array.from({ length: 12 }, (_, i) => ({ month: i + 1, year: selectedYear }));
  }
  const now = new Date();
  if (range === "1M") return Array.from({ length: 1 }, (_, i) => { const d = new Date(now.getFullYear(), now.getMonth() - i, 1); return { month: d.getMonth() + 1, year: d.getFullYear() }; });
  if (range === "3M") return Array.from({ length: 3 }, (_, i) => { const d = new Date(now.getFullYear(), now.getMonth() - (2 - i), 1); return { month: d.getMonth() + 1, year: d.getFullYear() }; });
  if (range === "6M") return Array.from({ length: 6 }, (_, i) => { const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1); return { month: d.getMonth() + 1, year: d.getFullYear() }; });
  if (range === "1Y") return Array.from({ length: 12 }, (_, i) => { const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1); return { month: d.getMonth() + 1, year: d.getFullYear() }; });
  return []; // ALL handled separately
};

const ml = (m: number, y: number) => `${MONTH_NAMES[m - 1].slice(0, 3)} ${y}`;

const barValueLabelPlugin = {
  id: "barValueLabels",
  afterDatasetsDraw(chart: any) {
    try {
      const { ctx } = chart;
      chart.data.datasets.forEach((ds: any, di: number) => {
        const meta = chart.getDatasetMeta(di);
        if (!meta?.data?.length) return;
        meta.data.forEach((bar: any, idx: number) => {
          const val = ds.data[idx];

          // Incident bars with _actuals: show "pct% (count)" or "N/A"
          if (ds._actuals) {
            const actual = ds._actuals[idx];
            let label: string;
            if (val == null && actual == null) {
              label = "N/A";
            } else {
              const pctStr = val != null ? `${val}%` : "N/A";
              const cntStr = actual != null ? ` (${actual})` : "";
              label = `${pctStr}${cntStr}`;
            }
            ctx.save();
            ctx.font = "bold 9px sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "bottom";
            ctx.fillStyle = "#1f2937";
            ctx.fillText(label, bar.x, bar.y - 2);
            ctx.restore();
            return;
          }

          if (val == null || val === 0) return;
          const label = ds.isCount ? `${val}` : `${val}%`;
          ctx.save();
          ctx.font = "bold 9px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillStyle = "#1f2937";
          ctx.fillText(label, bar.x, bar.y - 2);
          ctx.restore();
        });
      });
    } catch { /* never crash the chart */ }
  },
};

// ─── Chart base options ───────────────────────────────────────────────────────
const baseLineOpts = (title: string, yLabel: string, isPercent = false) => ({
  responsive: true, maintainAspectRatio: false,
  interaction: { mode: "index" as const, intersect: false },
  plugins: {
    legend: { position: "top" as const, labels: { font: { size: 11 }, padding: 14, usePointStyle: true } },
    title: { display: true, text: title, font: { size: 13, weight: "bold" as const }, padding: { bottom: 12 }, color: "#374151" },
    tooltip: { backgroundColor: "rgba(17,24,39,0.9)", padding: 10, cornerRadius: 8, callbacks: { label: (c: any) => `${c.dataset.label}: ${c.parsed.y != null ? c.parsed.y : "N/A"}${isPercent ? "%" : ""}` } },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 45 } },
    y: { beginAtZero: true, max: isPercent ? 100 : undefined, ticks: { font: { size: 10 }, callback: (v: any) => `${v}${isPercent ? "%" : ""}` }, title: { display: true, text: yLabel, font: { size: 10 } }, grid: { color: "rgba(0,0,0,0.06)" } },
  },
});

// grouped bar opts — extra top padding so on-bar labels don't clip
const groupedBarOpts = (title: string, yLabel: string, maxY = 110) => ({
  responsive: true, maintainAspectRatio: false,
  interaction: { mode: "index" as const, intersect: false },
  layout: { padding: { top: 24 } },
  plugins: {
    legend: { position: "top" as const, labels: { font: { size: 11 }, padding: 14, usePointStyle: true } },
    title: { display: true, text: title, font: { size: 13, weight: "bold" as const }, padding: { bottom: 12 }, color: "#374151" },
    tooltip: {
      backgroundColor: "rgba(17,24,39,0.9)", padding: 10, cornerRadius: 8,
      callbacks: { label: (c: any) => {
        // `${c.dataset.label}: ${c.parsed.y != null ? c.parsed.y : "N/A"}%`
          const pct = c.parsed.y;
          const actual = c.dataset._actuals?.[c.dataIndex];
          const pctStr = pct != null ? `${pct.toFixed(1)}%` : "N/A";
          const countStr = actual != null ? ` (${actual} incidents)` : "";
          return `${c.dataset.label}: ${pctStr}${actual != null ? countStr : ''}`;
      }}
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 45 } },
    y: { beginAtZero: true, max: maxY, ticks: { font: { size: 10 }, callback: (v: any) => `${v}%` }, title: { display: true, text: yLabel, font: { size: 10 } }, grid: { color: "rgba(0,0,0,0.06)" } },
  },
});

const baseBarOpts = (title: string, yLabel: string) => ({ ...baseLineOpts(title, yLabel, true) });

// ─── TrendsView ───────────────────────────────────────────────────────────────
const TrendsView: React.FC = () => {
  const { premiseId, reportType } = useParams<{ premiseId: string; reportType: string }>();
  const navigate = useNavigate();
  const { allPremises, fetchPremises } = usePremisesStore();

  const [allRecords, setAllRecords] = useState<any[]>([]);
  const [equipInspData, setEquipInspData] = useState<{ inspections: any[]; equipmentTypes: any[] }>({ inspections: [], equipmentTypes: [] });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>("6M");

  // ── Chart refs for PNG download ───────────────────────────────────────────
  const refFireSafety = useRef<any>(null);
  const refElectrical = useRef<any>(null);
  const refKpi = useRef<any>(null);
  const refSplitAc = useRef<any>(null);
  const refMis = useRef<any>(null);
  const refEquipCount = useRef<any>(null);
  const refEquipPass = useRef<any>(null);

  const downloadChart = useCallback((ref: React.RefObject<any>, name: string) => {
    const chart = ref.current;
    if (!chart) return;
    const src = chart.canvas as HTMLCanvasElement;
    const exp = document.createElement('canvas');
    exp.width = src.width;
    exp.height = src.height;
    const ctx = exp.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, exp.width, exp.height);
    ctx.drawImage(src, 0, 0);
    const a = document.createElement('a');
    a.href = exp.toDataURL('image/png');
    a.download = name;
    a.click();
  }, []);

  const premiseName = getPremiseName(premiseId ?? "", allPremises);
  const reportConfig = getReportTypeConfig(reportType ?? "");

  useEffect(() => { if (!allPremises.length) fetchPremises(); }, []);

  useEffect(() => {
    if (!premiseId || !reportConfig) return;
    setLoading(true);

    if (reportType === "equipment-inspections") {
      // Special fetch for equipment inspections — returns nested structure
      axiosInstance.get("/api/inspection-assessments/monitoring/by-premises", {
        params: { premisesId: premiseId },
      }).then(r => {
        const d = r.data?.data || r.data || {};
        setEquipInspData({
          inspections: d.inspections || [],
          equipmentTypes: d.equipmentTypes || [],
        });
      }).catch(() => setEquipInspData({ inspections: [], equipmentTypes: [] }))
        .finally(() => setLoading(false));
    } else {
      // Standard: fetch all records for this premise
      // Try both premisesId and premise as param names for compatibility
      Promise.all([
        safeFetchAll(reportConfig.apiEndpoint, { premisesId: premiseId }),
        safeFetchAll(reportConfig.apiEndpoint, { premise: premiseId }),
      ]).then(([r1, r2]) => {
        // Merge and deduplicate by _id
        const seen = new Set<string>();
        const merged: any[] = [];
        [...r1, ...r2].forEach(r => {
          const id = r._id || r.id || JSON.stringify(r);
          if (!seen.has(id)) { seen.add(id); merged.push(r); }
        });
        setAllRecords(merged);
      }).finally(() => setLoading(false));
    }
  }, [premiseId, reportType]);

  // ── Filename helper ───────────────────────────────────────────────────────
  const chartFileName = useCallback((label: string) => {
    const date = new Date().toISOString().slice(0, 10);
    const safe = (s: string) => s.replace(/[^a-zA-Z0-9]/g, '_');
    return `${safe(label)}_${safe(premiseName)}_${date}.png`;
  }, [premiseName]);

  // ─── Determine months to display ─────────────────────────────────────────
  const displayMonths = useMemo((): { month: number; year: number }[] => {
    const records = reportType === "equipment-inspections" ? equipInspData.inspections : allRecords;
    if (timeRange === "ALL") {
      const dates = records.map(r => getMonthYear(r.date || r.inspectionDate || r.createdAt || "")).filter(Boolean) as { month: number; year: number }[];
      if (!dates.length) return getMonthsForRange("1Y", null);
      const minYear = Math.min(...dates.map(d => d.year));
      const minMonth = Math.min(...dates.filter(d => d.year === minYear).map(d => d.month));
      const now = new Date();
      const result: { month: number; year: number }[] = [];
      let y = minYear, m = minMonth;
      while (y < now.getFullYear() || (y === now.getFullYear() && m <= now.getMonth() + 1)) {
        result.push({ month: m, year: y });
        m++; if (m > 12) { m = 1; y++; }
      }
      return result;
    }
    return getMonthsForRange(timeRange, null);
  }, [allRecords, equipInspData, timeRange, reportType]);

  // ─── Build data points for standard report types ──────────────────────────
  const dataPoints = useMemo((): MonthDataPoint[] => {
    if (reportType === "equipment-inspections") return [];
    return displayMonths.map(({ month, year }) => {
      const recs = allRecords.filter(r => {
        const my = getMonthYear(r.date || r.createdAt || "");
        return my && my.month === month && my.year === year;
      });
      const base: MonthDataPoint = { month, year, label: ml(month, year), count: recs.length };

      if (reportType === "fire-safety-scorecard" && recs.length) {
        const scored = recs.filter(r => r.fireSafetyParameters?.length);
        if (scored.length) {
          const pcts = scored.map(r => {
            const p = r.fireSafetyParameters || [];
            const ts = p.reduce((s: number, x: any) => s + (x.score ?? 0), 0);
            const tm = p.reduce((s: number, x: any) => s + x.maxScore, 0);
            return tm > 0 ? (ts / tm) * 100 : 0;
          });
          base.avgPct = pcts.reduce((s, v) => s + v, 0) / pcts.length;

          const nearMissPcts = scored
            .map(r => r.nearMissPct)
            .filter((v): v is number => v !== null && v !== undefined);

          base.nearMissPct = nearMissPcts.length
            ? nearMissPcts.reduce((s, v) => s + v, 0) / nearMissPcts.length
            : null;

          base.nearMissActual = scored.reduce(
            (sum, r) => sum + (r.nearMissActual ?? 0),
            0
          );

          const majorPcts = scored
            .map(r => r.majorPct)
            .filter((v): v is number => v !== null && v !== undefined);

          base.majorPct = majorPcts.length
            ? majorPcts.reduce((s, v) => s + v, 0) / majorPcts.length
            : null;

          base.majorActual = scored.reduce(
            (sum, r) => sum + (r.majorActual ?? 0),
            0
          );

          const combinedPcts = scored
            .map(r => {
              if (r.nearMissPct == null && r.majorPct == null) return null;
              return ((r.nearMissPct ?? 0) + (r.majorPct ?? 0)) / 2;
            })
            .filter((v): v is number => v !== null);

          base.incidentPct = combinedPcts.length
            ? combinedPcts.reduce((s, v) => s + v, 0) / combinedPcts.length
            : null;

          // const incidentPcts = scored.map(r => {
          //   const p = r.fireSafetyParameters || [];
          //   const sr45 = p.find((x: any) => x.misChecklistSrNos === 45);
          //   const sr46 = p.find((x: any) => x.misChecklistSrNos === 46);
          //   const actualTotal = (sr45?.score ?? 0) + (sr46?.score ?? 0);
          //   const targetTotal = (sr45?.maxScore ?? 0) + (sr46?.maxScore ?? 0);
          //   return targetTotal > 0 ? (actualTotal / targetTotal) * 100 : null;
          // }).filter((v): v is number => v !== null);

          // base.incidentPct = incidentPcts.length
          //   ? incidentPcts.reduce((s, v) => s + v, 0) / incidentPcts.length
          //   : null;

        }
        // External audit scores per category (avg across records in this month)
        const avgCatScore = (records: any[], field: "externalAuditRecords" | "internalAuditRecords", cat: string): number | null => {
          const vals = records.map(r => {
            const rec = (r[field] || []).find((a: any) => a.category === cat);
            return rec?.currentScore != null ? rec.currentScore : null;
          }).filter((v): v is number => v !== null);
          return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
        };
        const avgCatPct = (records: any[], field: "externalAuditRecords" | "internalAuditRecords"): number | null => {
          const vals = records.map(r => {
            const cats = r[field] || [];
            const ts = cats.reduce((s: number, a: any) => s + (a.currentScore ?? 0), 0);
            const tm = cats.reduce((s: number, a: any) => s + (a.maxScore ?? 0), 0);
            return tm > 0 ? (ts / tm) * 100 : null;
          }).filter((v): v is number => v !== null);
          return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
        };
        // Cat-A NC completion % — uses completionPercent field from the audit record
        const avgCatNcPct = (records: any[], field: "externalAuditRecords" | "internalAuditRecords", cat: string): number | null => {
          const vals = records.map(r => {
            const rec = (r[field] || []).find((a: any) => a.category === cat);
            return rec?.completionPercent != null ? rec.completionPercent : null;
          }).filter((v): v is number => v !== null);
          return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
        };
        base.extA = avgCatScore(recs, "externalAuditRecords", "A");
        base.extB = avgCatScore(recs, "externalAuditRecords", "B");
        base.extC = avgCatScore(recs, "externalAuditRecords", "C");
        base.intA = avgCatScore(recs, "internalAuditRecords", "A");
        base.intB = avgCatScore(recs, "internalAuditRecords", "B");
        base.intC = avgCatScore(recs, "internalAuditRecords", "C");
        base.extAvgPct = avgCatPct(recs, "externalAuditRecords");
        base.intAvgPct = avgCatPct(recs, "internalAuditRecords");
        base.extACatNcPct = avgCatNcPct(recs, "externalAuditRecords", "A");
        base.intACatNcPct = avgCatNcPct(recs, "internalAuditRecords", "A");
      } else if (reportType === "electrical-scorecard" && recs.length) {
        const scored = recs.filter(r => r.sections?.length);
        if (scored.length) {
          const pcts = scored.map(r => { const s = r.sections || []; const ts = s.reduce((a: number, x: any) => a + x.totalScore, 0); const tm = s.reduce((a: number, x: any) => a + x.totalMaxScore, 0); return tm > 0 ? (ts / tm) * 100 : 0; });
          base.avgPct = pcts.reduce((s, v) => s + v, 0) / pcts.length;
        }
      } else if (reportType === "kpi-scorecard" && recs.length) {
        const scored = recs.filter(r => r.compliedPct !== undefined || r.compliancePct !== undefined);
        if (scored.length) {
          base.avgCompliedPct = scored.reduce((s, r) => s + (r.compliedPct ?? 0), 0) / scored.length;
          base.avgNotCompliedPct = scored.reduce((s, r) => s + (r.notCompliedPct ?? 0), 0) / scored.length;
          base.avgPartialPct = scored.reduce((s, r) => s + (r.partiallyCompliedPct ?? 0), 0) / scored.length;
        }
      } else if (reportType === "split-ac" && recs.length) {
        const counts = recs.map(r => { const items = r.items || []; const c = items.filter((it: any) => it.status === "Complied").length; const n = items.filter((it: any) => it.status === "Not Complied").length; return { c, n, t: items.length }; });
        base.avgComplied = counts.reduce((s, c) => s + (c.t > 0 ? (c.c / c.t) * 100 : 0), 0) / counts.length;
        base.avgNotComplied = counts.reduce((s, c) => s + (c.t > 0 ? (c.n / c.t) * 100 : 0), 0) / counts.length;
      } else if (reportType === "mis-reports" && recs.length) {
        const fls = recs.map(r => { const items: any[] = r.fireAndLifeSafetyEquipment || []; const ti = items.reduce((s, it) => s + (it.installed ?? 0), 0); const tw = items.reduce((s, it) => s + (it.working ?? 0), 0); return ti > 0 ? (tw / ti) * 100 : null; }).filter(v => v !== null) as number[];
        const mep = recs.map(r => { const items: any[] = r.fireAndLifeSafetySystem || []; if (!items.length) return null; return (items.filter(it => (it.status || "").toUpperCase() === "YES").length / items.length) * 100; }).filter(v => v !== null) as number[];
        const drills = recs.map(r => { const items: any[] = r.fireIncidentDrills || []; const valid = items.filter(it => it.targetNo > 0); if (!valid.length) return null; const ta = valid.reduce((s, it) => s + (it.actualNo ?? 0), 0); const tt = valid.reduce((s, it) => s + it.targetNo, 0); return tt > 0 ? (ta / tt) * 100 : null; }).filter(v => v !== null) as number[];
        // T4 (ERT) removed — no longer shown
        // T5: Incident % = (actualNo of sr45 + sr46) / (targetNo of sr45 + sr46) * 100
        const incidents = recs.map(r => {
          const items: any[] = r.fireIncidentDrills || [];
          const sr45 = items.find((it: any) => it.srNo === 45);
          const sr46 = items.find((it: any) => it.srNo === 46);
          const totalActual = (sr45?.actualNo ?? 0) + (sr46?.actualNo ?? 0);
          const totalTarget = (sr45?.targetNo ?? 0) + (sr46?.targetNo ?? 0);
          return totalTarget > 0 ? (totalActual / totalTarget) * 100 : null;
        }).filter(v => v !== null) as number[];

        // Near miss % and count per record
        const nearMissPcts = recs.map(r => {
          const items: any[] = r.fireIncidentDrills || [];
          // Look by srNo (60/45) or by system name containing "NEAR MISS"
          const row = items.find((it: any) =>
            it.srNo === 60 || it.srNo === 45 ||
            it.systems?.toUpperCase().includes("NEAR MISS")
          );
          if (!row) return null;
          const actual = row.actualNo ?? null;
          const target = row.targetNo ?? null;
          const pct = (target != null && target > 0 && actual != null)
            ? (actual / target) * 100 : null;
          return { pct, actual };
        }).filter(v => v !== null) as { pct: number | null; actual: number | null }[];

        // Major accidents % and count per record
        const majorPcts = recs.map(r => {
          const items: any[] = r.fireIncidentDrills || [];
          const row = items.find((it: any) =>
            it.srNo === 61 || it.srNo === 46 ||
            it.systems?.toUpperCase().includes("MAJOR ACCIDENT")
          );
          if (!row) return null;
          const actual = row.actualNo ?? null;
          const target = row.targetNo ?? null;
          const pct = (target != null && target > 0 && actual != null)
            ? (actual / target) * 100 : null;
          return { pct, actual };
        }).filter(v => v !== null) as { pct: number | null; actual: number | null }[];

        // Also read from incidentSummary if available (preferred source)
        const nmFromSummary = recs.map(r => {
          const summary: any[] = r.incidentSummary || [];
          const nearRow = summary.find((s: any) =>
            (s.description || "").toUpperCase().includes("NEAR MISS")
          );
          return nearRow?.count ?? null;
        }).filter(v => v !== null) as number[];

        const majFromSummary = recs.map(r => {
          const summary: any[] = r.incidentSummary || [];
          const majRow = summary.find((s: any) =>
            (s.description || "").toUpperCase().includes("MAJOR")
          );
          return majRow?.count ?? null;
        }).filter(v => v !== null) as number[];

        base.avgComplied = fls.length ? fls.reduce((s, v) => s + v, 0) / fls.length : undefined;
        base.avgNotComplied = mep.length ? mep.reduce((s, v) => s + v, 0) / mep.length : undefined;
        base.avgPartialPct = drills.length ? drills.reduce((s, v) => s + v, 0) / drills.length : undefined;
        // avgCompliedPct (T4/ERT) no longer used
        base.avgNotCompliedPct = incidents.length ? incidents.reduce((s, v) => s + v, 0) / incidents.length : undefined;

        // Near miss: prefer incidentSummary count, else fireIncidentDrills actual
        const nmActualArr = nmFromSummary.length ? nmFromSummary
          : nearMissPcts.map(x => x.actual).filter((v): v is number => v !== null);
        base.nearMissActual = nmActualArr.length
          ? nmActualArr.reduce((s, v) => s + v, 0) / nmActualArr.length : null;

        const nmPctArr = nearMissPcts.map(x => x.pct).filter((v): v is number => v !== null);
        base.nearMissPct = nmPctArr.length
          ? nmPctArr.reduce((s, v) => s + v, 0) / nmPctArr.length : null;

        // Major: prefer incidentSummary count, else fireIncidentDrills actual
        const majActualArr = majFromSummary.length ? majFromSummary
          : majorPcts.map(x => x.actual).filter((v): v is number => v !== null);
        base.majorActual = majActualArr.length
          ? majActualArr.reduce((s, v) => s + v, 0) / majActualArr.length : null;

        const majPctArr = majorPcts.map(x => x.pct).filter((v): v is number => v !== null);
        base.majorPct = majPctArr.length
          ? majPctArr.reduce((s, v) => s + v, 0) / majPctArr.length : null;
      }
      return base;
    });
  }, [allRecords, displayMonths, reportType]);

  // ─── Equipment inspection trends per type ─────────────────────────────────
  const equipTrends = useMemo((): EquipTypeTrend[] => {
    if (reportType !== "equipment-inspections") return [];
    const { inspections, equipmentTypes } = equipInspData;

    // Build name map: id → human-readable name
    // equipmentTypes from backend: { id: asset.equipmentInventory, name: asset.equipmentInventoryName || asset.equipmentInventory }
    const nameMap: Record<string, string> = {};
    equipmentTypes.forEach(et => {
      // Prefer the name field; fall back to id only if name looks like an ObjectId
      const isRawId = /^[a-f0-9]{24}$/i.test(et.name || "");
      nameMap[et.id] = (!isRawId && et.name) ? et.name : (et.name || et.id);
    });

    // Group inspections by equipment type — use equipmentInventoryName as the key
    // because equipmentInventoryId may be an ObjectId or the category name string
    const byType: Record<string, { name: string; insps: any[] }> = {};
    inspections.forEach(insp => {
      // Prefer the human-readable name as the grouping key
      const typeName = insp.equipmentInventoryName || "";
      const typeId = insp.equipmentInventoryId || typeName || "unknown";
      const resolvedName = nameMap[typeId] || typeName || typeId;
      const key = typeName || typeId; // group by name if available
      if (!byType[key]) byType[key] = { name: resolvedName, insps: [] };
      byType[key].insps.push(insp);
    });

    return Object.entries(byType).map(([key, { name, insps }]) => {
      const data = displayMonths.map(({ month, year }) => {
        const monthInsps = insps.filter(insp => {
          const my = getMonthYear(insp.inspectionDate || insp.createdAt || "");
          return my && my.month === month && my.year === year;
        });
        const passCount = monthInsps.filter(i => i.overallStatus === "pass").length;
        const passPct = monthInsps.length > 0 ? Math.round((passCount / monthInsps.length) * 100) : null;
        return { month, year, label: ml(month, year), count: monthInsps.length, passPct };
      });
      return { id: key, name, data };
    }).filter(et => et.data.some(d => d.count > 0));
  }, [equipInspData, displayMonths, reportType]);

  const hasData = reportType === "equipment-inspections"
    ? equipTrends.some(et => et.data.some(d => d.count > 0))
    : dataPoints.some(d => d.count > 0);

  const labels = displayMonths.map(({ month, year }) => ml(month, year));
  const totalRecords = reportType === "equipment-inspections"
    ? equipInspData.inspections.filter(insp => { const my = getMonthYear(insp.inspectionDate || insp.createdAt || ""); return my && displayMonths.some(dm => dm.month === my.month && dm.year === my.year); }).length
    : dataPoints.reduce((s, d) => s + d.count, 0);
  const activeMonths = reportType === "equipment-inspections"
    ? displayMonths.filter(({ month, year }) => equipInspData.inspections.some(insp => { const my = getMonthYear(insp.inspectionDate || insp.createdAt || ""); return my && my.month === month && my.year === year; })).length
    : dataPoints.filter(d => d.count > 0).length;

  if (!reportConfig) {
    return (
      <div className="w-full px-6 mt-20 pb-10 flex flex-col items-center justify-center py-24 text-center">
        <h3 className="text-xl font-bold text-gray-800 mb-2">Unknown report type</h3>
        <button onClick={() => navigate(`/fire-system/monitoring/${premiseId}`)} className="mt-4 px-5 py-2.5 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] font-medium">Back</button>
      </div>
    );
  }

  // ─── Chart datasets ──────────────────────────────────────────────────────
  const recordCountChart = { labels, datasets: [{ label: "Records", data: dataPoints.map(d => d.count), backgroundColor: "rgba(0,168,165,0.12)", borderColor: "#00A8A5", borderWidth: 2.5, fill: true, tension: 0.4, pointBackgroundColor: "#00A8A5", pointRadius: 5, pointHoverRadius: 7 }] };

  // ── Fire Safety Scorecard — 3 grouped bars per month ─────────────────────
  // Bar 1: External Cat-A NC Completion % (red)
  // Bar 2: Internal Cat-A NC Completion % (purple)
  // Bar 3: Fire Safety Parameters Aggregate % (teal)
  const fireSafetyBarData = {
    labels,
    datasets: [
      {
        label: "% External Audit (Cat-A NC Completion)",
        data: dataPoints.map(d => d.extACatNcPct != null ? +d.extACatNcPct.toFixed(1) : null),
        backgroundColor: "rgba(239,68,68,0.80)",
        borderColor: "#dc2626",
        borderWidth: 1.5,
        borderRadius: 4,
        barPercentage: 0.75,
        categoryPercentage: 0.8,
      },
      {
        label: "% Internal Audit (Cat-A NC Completion)",
        data: dataPoints.map(d => d.intACatNcPct != null ? +d.intACatNcPct.toFixed(1) : null),
        backgroundColor: "rgba(251,113,133,0.85)",
        borderColor: "rgba(244,63,94,1)",
        borderWidth: 1.5,
        borderRadius: 4,
        barPercentage: 0.75,
        categoryPercentage: 0.8,
      },
      {
        label: "Fire Safety Aggregate % Score",
        data: dataPoints.map(d => d.avgPct != null ? +d.avgPct.toFixed(1) : null),
        backgroundColor: "rgba(231, 108, 13, 0.8)",
        borderColor: "#e89624ff",
        borderWidth: 1.5,
        borderRadius: 4,
        barPercentage: 0.75,
        categoryPercentage: 0.8,
      },
      {
        label: 'Near Miss %',
        data: dataPoints.map(d => d.nearMissPct != null ? +d.nearMissPct.toFixed(1) : null),
        _actuals: dataPoints.map(d => d.nearMissActual ?? null),
        backgroundColor: 'rgba(68, 202, 239, 0.8)',
        borderColor: 'rgba(28, 185, 174, 1)',
        borderWidth: 1.5,
        borderRadius: 4,
        barPercentage: 0.75,
        categoryPercentage: 0.8,
      },
      {
        label: 'Major Incident %',
        data: dataPoints.map(d => d.majorPct != null ? +d.majorPct.toFixed(1) : null),
        _actuals: dataPoints.map(d => d.majorActual ?? null),
        backgroundColor: 'rgba(99,102,241,0.80)',    // indigo
        borderColor: 'rgba(79,70,229,1)',
        borderWidth: 1.5,
        borderRadius: 4,
        barPercentage: 0.75,
        categoryPercentage: 0.8,
      },
    ],
  };

  // ── Electrical Scorecard — single bar per month, score + % on bar ─────────
  const electricalBarData = {
    labels,
    datasets: [
      {
        label: "Avg score %",
        data: dataPoints.map(d => d.avgPct != null ? +d.avgPct.toFixed(1) : null),
        backgroundColor: dataPoints.map(d =>
          d.avgPct == null ? "rgba(156,163,175,0.5)"
            : d.avgPct >= 80 ? "rgba(16,185,129,0.80)"
              : d.avgPct >= 60 ? "rgba(245,158,11,0.80)"
                : "rgba(239,68,68,0.80)"
        ),
        borderColor: dataPoints.map(d =>
          d.avgPct == null ? "#9ca3af"
            : d.avgPct >= 80 ? "#059669"
              : d.avgPct >= 60 ? "#d97706"
                : "#dc2626"
        ),
        borderWidth: 1.5,
        borderRadius: 4,
        barPercentage: 0.55,
        categoryPercentage: 0.8,
      },
    ],
  };

  const kpiChart = {
    labels, datasets: [
      { label: "Complied %", data: dataPoints.map(d => d.avgCompliedPct != null ? +d.avgCompliedPct.toFixed(1) : null), backgroundColor: "rgba(16,185,129,0.75)", borderColor: "#10b981", borderWidth: 1, spanGaps: true },
      { label: "Partially %", data: dataPoints.map(d => d.avgPartialPct != null ? +d.avgPartialPct.toFixed(1) : null), backgroundColor: "rgba(245,158,11,0.75)", borderColor: "#f59e0b", borderWidth: 1, spanGaps: true },
      { label: "Not Complied %", data: dataPoints.map(d => d.avgNotCompliedPct != null ? +d.avgNotCompliedPct.toFixed(1) : null), backgroundColor: "rgba(239,68,68,0.75)", borderColor: "#ef4444", borderWidth: 1, spanGaps: true },
    ]
  };
  const splitAcChart = {
    labels, datasets: [
      { label: "Complied %", data: dataPoints.map(d => d.avgComplied != null ? +d.avgComplied.toFixed(1) : null), backgroundColor: "rgba(16,185,129,0.75)", borderColor: "#10b981", borderWidth: 1, spanGaps: true },
      { label: "Not Complied %", data: dataPoints.map(d => d.avgNotComplied != null ? +d.avgNotComplied.toFixed(1) : null), backgroundColor: "rgba(239,68,68,0.75)", borderColor: "#ef4444", borderWidth: 1, spanGaps: true },
    ]
  };
  const misCombinedChart = {
    labels,
    datasets: [
      {
        label: "FLS Equipment %",
        data: dataPoints.map(d => d.avgComplied != null ? +d.avgComplied.toFixed(1) : null),
        backgroundColor: "rgba(59,130,246,0.88)",
        borderColor: "rgba(37,99,235,1)",
        borderWidth: 1.5, borderRadius: 3,
        categoryPercentage: 1.0, barPercentage: 0.19,
      },
      {
        label: "FLS Systems %",
        data: dataPoints.map(d => d.avgNotComplied != null ? +d.avgNotComplied.toFixed(1) : null),
        backgroundColor: "rgba(249,115,22,0.88)",
        borderColor: "rgba(234,88,12,1)",
        borderWidth: 1.5, borderRadius: 3,
        categoryPercentage: 1.0, barPercentage: 0.19,
      },
      {
        label: "Drills & Trainings %",
        data: dataPoints.map(d => d.avgPartialPct != null ? +d.avgPartialPct.toFixed(1) : null),
        backgroundColor: "rgba(168,85,247,0.88)",
        borderColor: "rgba(126,34,206,1)",
        borderWidth: 1.5, borderRadius: 3,
        categoryPercentage: 1.0, barPercentage: 0.19,
      },
      {
        // Near Miss % — show pct on bar, count in brackets
        label: "Near Miss %",
        data: dataPoints.map(d => d.nearMissPct != null ? +d.nearMissPct.toFixed(1) : null),
        _actuals: dataPoints.map(d => d.nearMissActual ?? null),
        backgroundColor: "rgba(239,68,68,0.88)",
        borderColor: "rgba(220,38,38,1)",
        borderWidth: 1.5, borderRadius: 3,
        categoryPercentage: 1.0, barPercentage: 0.19,
      },
      {
        // Major Incident % — show pct on bar, count in brackets
        label: "Major Incident %",
        data: dataPoints.map(d => d.majorPct != null ? +d.majorPct.toFixed(1) : null),
        _actuals: dataPoints.map(d => d.majorActual ?? null),
        backgroundColor: "rgba(99,102,241,0.88)",
        borderColor: "rgba(79,70,229,1)",
        borderWidth: 1.5, borderRadius: 3,
        categoryPercentage: 1.0, barPercentage: 0.19,
      },
    ],
  };

  const misBarOpts = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 32 } },
    interaction: { mode: "index" as const, intersect: false },
    plugins: {
      legend: {
        position: "top" as const,
        labels: { font: { size: 11 }, padding: 14, usePointStyle: true },
      },
      title: {
        display: true,
        text: "MIS Report — Monthly Metrics Overview",
        font: { size: 13, weight: "bold" as const },
        padding: { bottom: 12 },
        color: "#374151",
      },
      tooltip: {
        backgroundColor: "rgba(17,24,39,0.9)",
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (c: any) => {
            const pct = c.parsed.y;
            const actual = c.dataset._actuals?.[c.dataIndex];
            const pctStr = pct != null ? `${pct.toFixed(1)}%` : "N/A";
            const countStr = actual != null ? ` (${actual} incidents)` : "";
            return `${c.dataset.label}: ${pctStr}${countStr}`;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 45 } },
      y: {
        beginAtZero: true,
        max: 110,
        ticks: { font: { size: 10 }, callback: (v: any) => `${v}%` },
        title: { display: true, text: "Percentage (%)", font: { size: 10 } },
        grid: { color: "rgba(0,0,0,0.06)" },
      },
    },
  };

  // Equipment inspection charts — one line per equipment type
  const EQUIP_COLORS = ["#00A8A5", "#3b82f6", "#f59e0b", "#a855f7", "#10b981", "#ef4444", "#ec4899", "#14b8a6", "#f97316", "#6366f1"];
  const equipPassRateChart = { labels, datasets: equipTrends.map((et, i) => ({ label: et.name, data: et.data.map(d => d.passPct), borderColor: EQUIP_COLORS[i % EQUIP_COLORS.length], backgroundColor: `${EQUIP_COLORS[i % EQUIP_COLORS.length]}18`, borderWidth: 2, fill: false, tension: 0.4, pointRadius: 4, pointHoverRadius: 6, spanGaps: true })) };
  const equipCountChart = { labels, datasets: equipTrends.map((et, i) => ({ label: et.name, data: et.data.map(d => d.count), backgroundColor: `${EQUIP_COLORS[i % EQUIP_COLORS.length]}cc`, borderColor: EQUIP_COLORS[i % EQUIP_COLORS.length], borderWidth: 1, spanGaps: true, isCount: true })) };

  return (
    <div className="w-full px-6 mt-20 pb-10">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="flex flex-wrap items-center gap-1 text-sm text-gray-500">
          <li><Link to="/fire-system/monitoring" className="hover:text-[#00A8A5] font-medium">Monitoring</Link></li>
          <li className="text-gray-300">›</li>
          <li><Link to={`/fire-system/monitoring/${premiseId}`} className="hover:text-[#00A8A5] truncate max-w-[120px] inline-block">{premiseName}</Link></li>
          <li className="text-gray-300">›</li>
          <li><Link to={`/fire-system/monitoring/${premiseId}/${reportType}`} className="hover:text-[#00A8A5]">{reportConfig.label}</Link></li>
          <li className="text-gray-300">›</li>
          <li aria-current="page" className="text-gray-800 font-semibold">Trends</li>
        </ol>
      </nav>

      <button onClick={() => navigate(`/fire-system/monitoring/${premiseId}/${reportType}`)} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#00A8A5] transition-colors mb-5 group">
        <span className="group-hover:-translate-x-0.5 transition-transform">←</span>Back to months
      </button>

      {/* Header + controls */}
      <div className="flex flex-col gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trends — {reportConfig.label}</h1>
          <p className="text-gray-500 text-sm mt-1"><span className="font-semibold text-gray-700">{premiseName}</span> · Time-series analysis</p>
        </div>

        {/* Controls row: Time range */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
            {(["1M", "3M", "6M", "1Y", "ALL"] as TimeRange[]).map(r => (
              <button key={r} onClick={() => setTimeRange(r)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${timeRange === r ? "bg-white text-[#00A8A5] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                {r}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-24"><DotLoader /></div>
      ) : !hasData ? (
        <div className="flex flex-col items-center justify-center py-24 text-center bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No data available</h3>
          <p className="text-gray-400 text-sm max-w-sm">
            No records found for the selected time range. Try selecting a wider range.
          </p>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {[
              { label: "Total Records", value: totalRecords, sub: "in selected period", color: "text-[#00A8A5]", bg: "bg-[#00A8A5]/10" },
              { label: "Active Months", value: activeMonths, sub: `of ${displayMonths.length} months`, color: "text-blue-600", bg: "bg-blue-50" },
              { label: "Period", value: timeRange === "ALL" ? "All time" : timeRange, sub: "Rolling window", color: "text-purple-600", bg: "bg-purple-50" },
            ].map(card => (
              <div key={card.label} className={`rounded-xl p-5 ${card.bg} border border-transparent`}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{card.label}</p>
                <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
                <p className="text-xs text-gray-400 mt-0.5">{card.sub}</p>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="flex flex-col gap-6">
            {/* Standard report types — record count */}
            {reportType !== "equipment-inspections" && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="h-64"><Line data={recordCountChart} options={baseLineOpts(`${reportConfig.label} — Records per Month`, "Records")} /></div>
              </div>
            )}

            {/* ── Fire Safety Scorecard — 3 grouped bars per month ── */}
            {reportType === "fire-safety-scorecard" && dataPoints.some(d => d.avgPct != null || d.extACatNcPct != null || d.intACatNcPct != null) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700">Fire Safety Scorecard — Monthly Overview</span>
                  <button onClick={() => downloadChart(refFireSafety, chartFileName("Fire_Safety_Scorecard_Monthly"))} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#00A8A5] border border-[#00A8A5] rounded-lg hover:bg-[#00A8A5] hover:text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    Download PNG
                  </button>
                </div>
                <div className="h-80">
                  <Bar
                    ref={refFireSafety}
                    data={fireSafetyBarData}
                    options={groupedBarOpts("Fire Safety Scorecard — Monthly Overview", "Percentage (%)", 110) as any}
                    plugins={[barValueLabelPlugin]}
                  />
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  {[
                    { color: "rgba(239,68,68,0.80)", border: "#dc2626", label: "% External Audit (Cat-A NC Completion)" },
                    { color: "rgba(251,113,133,0.85)", border: "rgba(244,63,94,1)", label: "% Internal Audit (Cat-A NC Completion)" },
                    { color: "rgba(231, 108, 13, 0.8)", border: "#e89624ff", label: "Fire Safety Aggregate % Score" },
                    { color: 'rgba(68, 202, 239, 0.8)', border: 'rgba(28, 185, 174, 1)', label: "Near Miss %" },
                    { color: "rgba(99,102,241,0.80)", border: "rgba(79,70,229,1)", label: "Major Incident %" }
                  ].map(item => (
                    <div key={item.label} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
                      <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color, border: `1.5px solid ${item.border}` }} />
                      <span className="text-xs font-medium text-gray-600">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Electrical Scorecard — bar per month with % on bar ── */}
            {reportType === "electrical-scorecard" && dataPoints.some(d => d.avgPct != null) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700">Electrical Scorecard — Avg score % per Month</span>
                  <button onClick={() => downloadChart(refElectrical, chartFileName("Electrical_Scorecard_Monthly"))} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#00A8A5] border border-[#00A8A5] rounded-lg hover:bg-[#00A8A5] hover:text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    Download PNG
                  </button>
                </div>
                <div className="h-80">
                  <Bar
                    ref={refElectrical}
                    data={electricalBarData}
                    options={groupedBarOpts("Electrical Scorecard — Avg score % per Month", "Compliance (%)", 110) as any}
                    plugins={[barValueLabelPlugin]}
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm inline-block bg-green-500" /> ≥ 80% Good</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm inline-block bg-yellow-400" /> 60–79% Moderate</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm inline-block bg-red-500" /> &lt; 60% Needs Attention</span>
                </div>
              </div>
            )}

            {/* KPI */}
            {reportType === "kpi-scorecard" && dataPoints.some(d => d.avgCompliedPct != null) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700">KPI Compliance Breakdown by Month</span>
                  <button onClick={() => downloadChart(refKpi, chartFileName("KPI_Compliance_Monthly"))} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#00A8A5] border border-[#00A8A5] rounded-lg hover:bg-[#00A8A5] hover:text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    Download PNG
                  </button>
                </div>
                <div className="h-72"><Bar ref={refKpi} data={kpiChart} options={baseBarOpts("KPI Compliance Breakdown by Month", "Percentage %")} plugins={[barValueLabelPlugin]} /></div>
              </div>
            )}

            {/* Split AC */}
            {reportType === "split-ac" && dataPoints.some(d => d.avgComplied != null) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700">Split AC Compliance % by Month</span>
                  <button onClick={() => downloadChart(refSplitAc, chartFileName("Split_AC_Compliance_Monthly"))} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#00A8A5] border border-[#00A8A5] rounded-lg hover:bg-[#00A8A5] hover:text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    Download PNG
                  </button>
                </div>
                <div className="h-72"><Bar ref={refSplitAc} data={splitAcChart} options={baseBarOpts("Split AC Compliance % by Month", "Percentage %")} plugins={[barValueLabelPlugin]} /></div>
              </div>
            )}

            {/* MIS — grouped bar chart, one bar per metric per month */}
            {reportType === "mis-reports" && dataPoints.some(d => d.avgComplied != null || d.avgNotComplied != null || d.avgPartialPct != null) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700">MIS Report — Monthly Metrics Overview</span>
                  <button onClick={() => downloadChart(refMis, chartFileName("MIS_Report_Monthly"))} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#00A8A5] border border-[#00A8A5] rounded-lg hover:bg-[#00A8A5] hover:text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    Download PNG
                  </button>
                </div>
                <div className="h-96">
                  <Bar ref={refMis} data={misCombinedChart} options={misBarOpts as any} plugins={[barValueLabelPlugin]} />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {[
                    { color: "rgba(59,130,246,0.88)", border: "rgba(37,99,235,1)", label: "FLS Equipment %", desc: "Fire & Life Safety equipment" },
                    { color: "rgba(249,115,22,0.88)", border: "rgba(234,88,12,1)", label: "FLS Systems %", desc: "Fire and Life safety system" },
                    { color: "rgba(168,85,247,0.88)", border: "rgba(126,34,206,1)", label: "Drills & Trainings %", desc: "Fire incident / drills & training" },
                    { color: "rgba(239,68,68,0.88)", border: "rgba(220,38,38,1)", label: "Near Miss %", desc: "pct% (count) — actual vs target" },
                    { color: "rgba(99,102,241,0.88)", border: "rgba(79,70,229,1)", label: "Major Incident %", desc: "pct% (count) — actual vs target" },
                  ].map(item => (
                    <div key={item.label} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
                      <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color, border: `1.5px solid ${item.border}` }} />
                      <div>
                        <span className="text-xs font-semibold text-gray-700">{item.label}</span>
                        <span className="text-xs text-gray-400 ml-1">— {item.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Equipment Inspections — pass rate per type */}
            {reportType === "equipment-inspections" && equipTrends.length > 0 && (
              <>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700">Inspection Count by Equipment Type</h3>
                    <button onClick={() => downloadChart(refEquipCount, chartFileName("Equipment_Inspection_Count"))} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#00A8A5] border border-[#00A8A5] rounded-lg hover:bg-[#00A8A5] hover:text-white transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      Download PNG
                    </button>
                  </div>
                  <div className="h-72"><Bar ref={refEquipCount} data={equipCountChart} options={baseBarOpts("Inspections per Month by Equipment Type", "Count")} plugins={[barValueLabelPlugin]} /></div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700">Pass Rate % by Equipment Type</h3>
                    <button onClick={() => downloadChart(refEquipPass, chartFileName("Equipment_Pass_Rate"))} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#00A8A5] border border-[#00A8A5] rounded-lg hover:bg-[#00A8A5] hover:text-white transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      Download PNG
                    </button>
                  </div>
                  <div className="h-72"><Line ref={refEquipPass} data={equipPassRateChart} options={baseLineOpts("Pass Rate % per Month by Equipment Type", "Pass %", true)} /></div>
                </div>
              </>
            )}

            {/* Monthly breakdown table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">Monthly Breakdown</h3>
                <span className="text-xs text-gray-400">{displayMonths.length} months shown</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#E9F5FF]">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">Month</th>
                      <th className="px-5 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wide">Records</th>
                      {(reportType === "fire-safety-scorecard" || reportType === "electrical-scorecard") && <th className="px-5 py-3 text-center text-xs font-bold text-gray-600 uppercase">Avg Score %</th>}
                      {reportType === "kpi-scorecard" && <><th className="px-5 py-3 text-center text-xs font-bold text-green-700 uppercase">Complied %</th><th className="px-5 py-3 text-center text-xs font-bold text-yellow-700 uppercase">Partial %</th><th className="px-5 py-3 text-center text-xs font-bold text-red-700 uppercase">Not Complied %</th></>}
                      {reportType === "split-ac" && <><th className="px-5 py-3 text-center text-xs font-bold text-green-700 uppercase">Complied %</th><th className="px-5 py-3 text-center text-xs font-bold text-red-700 uppercase">Not Complied %</th></>}
                      {reportType === "equipment-inspections" && equipTrends.map(et => <th key={et.id} className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase whitespace-nowrap">{et.name}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {displayMonths.map(({ month, year }, i) => {
                      const d = dataPoints[i] || { count: 0, label: ml(month, year) };
                      const hasAny = reportType === "equipment-inspections"
                        ? equipTrends.some(et => et.data[i]?.count > 0)
                        : d.count > 0;
                      return (
                        <tr key={i} className={`${!hasAny ? "opacity-40" : ""} ${i % 2 === 0 ? "bg-white" : "bg-gray-50/40"} hover:bg-[#00A8A5]/5 transition-colors`}>
                          <td className="px-5 py-3 font-medium text-gray-800">
                            <div className="flex items-center gap-2">
                              {hasAny && <div className="w-2 h-2 rounded-full bg-[#00A8A5]" />}
                              {ml(month, year)}
                            </div>
                          </td>
                          <td className="px-5 py-3 text-center">
                            {d.count > 0 ? (
                              <button onClick={() => navigate(`/fire-system/monitoring/${premiseId}/${reportType}/${month}`)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#00A8A5]/10 text-[#00A8A5] rounded-full text-xs font-semibold hover:bg-[#00A8A5]/20 transition-colors">
                                {d.count} →
                              </button>
                            ) : <span className="text-gray-300 text-xs">—</span>}
                          </td>
                          {(reportType === "fire-safety-scorecard" || reportType === "electrical-scorecard") && (
                            <td className="px-5 py-3 text-center">
                              {d.avgPct != null ? <span className={`font-semibold ${d.avgPct >= 80 ? "text-green-600" : d.avgPct >= 60 ? "text-yellow-600" : "text-red-600"}`}>{d.avgPct.toFixed(1)}%</span> : <span className="text-gray-300 text-xs">—</span>}
                            </td>
                          )}
                          {reportType === "kpi-scorecard" && (
                            <><td className="px-5 py-3 text-center font-semibold text-green-600">{d.avgCompliedPct != null ? `${d.avgCompliedPct.toFixed(1)}%` : <span className="text-gray-300 text-xs">—</span>}</td><td className="px-5 py-3 text-center font-semibold text-yellow-600">{d.avgPartialPct != null ? `${d.avgPartialPct.toFixed(1)}%` : <span className="text-gray-300 text-xs">—</span>}</td><td className="px-5 py-3 text-center font-semibold text-red-600">{d.avgNotCompliedPct != null ? `${d.avgNotCompliedPct.toFixed(1)}%` : <span className="text-gray-300 text-xs">—</span>}</td></>
                          )}
                          {reportType === "split-ac" && (
                            <><td className="px-5 py-3 text-center font-semibold text-green-600">{d.avgComplied != null ? `${d.avgComplied.toFixed(1)}%` : <span className="text-gray-300 text-xs">—</span>}</td><td className="px-5 py-3 text-center font-semibold text-red-600">{d.avgNotComplied != null ? `${d.avgNotComplied.toFixed(1)}%` : <span className="text-gray-300 text-xs">—</span>}</td></>
                          )}
                          {reportType === "equipment-inspections" && equipTrends.map(et => {
                            const dp = et.data[i];
                            return (
                              <td key={et.id} className="px-4 py-3 text-center">
                                {dp?.count > 0 ? (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <span className="text-xs font-semibold text-gray-700">{dp.count} insp.</span>
                                    {dp.passPct != null && <span className={`text-xs font-bold ${dp.passPct >= 80 ? "text-green-600" : dp.passPct >= 60 ? "text-yellow-600" : "text-red-600"}`}>{dp.passPct}% pass</span>}
                                  </div>
                                ) : <span className="text-gray-300 text-xs">—</span>}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TrendsView;
